<script setup lang="ts">
import {
  compareRate,
  CurrencyCode,
  formatLocalDate,
  type Money,
  numberFormatFor,
  parseLocalDate,
  rate,
  systemClock,
  todayUtc,
} from '@taler/domain'
import { ActionButton, DecimalInput, Dialog, Select, StatusTag, TextField } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import LoanTermsFields from '@/features/loans/components/LoanTermsFields.vue'
import type { CreateLoanInput, LoanRateType, LoanType } from '../internal/types.ts'

const props = defineProps<{ isSubmitting: boolean; errorMessage: string | undefined }>()
const emit = defineEmits<{ create: [input: CreateLoanInput] }>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const numberFormat = computed(() => numberFormatFor(currentLocale.value))
const open = ref(false)
const name = ref('')
const currency = ref<CurrencyCode | undefined>(undefined)
const principal = ref<Money | null>(null)
// The interest rate is a Rate, not a Money - it goes through the plain DecimalInput, never
// through the currency-typed AmountInput, and is only parsed into a Rate at submit.
const annualInterestRateText = ref('')
const drawdownDate = ref('')
const endDate = ref('')
const lender = ref('')
const rateType = ref<LoanRateType | undefined>(undefined)
const loanType = ref<LoanType | undefined>(undefined)
const monthlyPayment = ref<Money | null>(null)
const todayDate = formatLocalDate(todayUtc(systemClock))
const MIN_DATE = '1900-01-01'
const MIN_RATE = '0'
const MAX_RATE = '100'
const validationError = ref<string | undefined>(undefined)

function reset(): void {
  name.value = ''
  currency.value = undefined
  principal.value = null
  annualInterestRateText.value = ''
  drawdownDate.value = ''
  endDate.value = ''
  lender.value = ''
  rateType.value = undefined
  loanType.value = undefined
  monthlyPayment.value = null
  validationError.value = undefined
}

function submit(): void {
  validationError.value = undefined
  if (name.value.trim() === '' || currency.value === undefined || principal.value === null) return
  try {
    const interestRate =
      annualInterestRateText.value === ''
        ? undefined
        : rate(annualInterestRateText.value, currency.value, currency.value)
    const bounds = {
      min: rate(MIN_RATE, currency.value, currency.value),
      max: rate(MAX_RATE, currency.value, currency.value),
    }
    if (
      interestRate !== undefined &&
      (compareRate(interestRate, bounds.min) < 0 || compareRate(interestRate, bounds.max) > 0)
    ) {
      validationError.value = t('loans.invalidInterestRate')
      return
    }
    const trimmedLender = lender.value.trim()
    const input: CreateLoanInput = {
      name: name.value.trim(),
      originalPrincipal: principal.value,
      annualInterestRate: interestRate,
      drawdownDate: parseLocalDate(drawdownDate.value),
      endDate: endDate.value === '' ? undefined : parseLocalDate(endDate.value),
      lender: trimmedLender === '' ? undefined : trimmedLender,
      rateType: rateType.value,
      loanType: loanType.value,
      monthlyPayment: monthlyPayment.value ?? undefined,
    }
    emit('create', input)
  } catch {
    validationError.value = t('loans.invalidAmount')
  }
}

// A submission that succeeded clears the form and closes it; the composable's mutation
// state is the only source of truth for "did it work", so this watches it rather than
// guessing from a local flag.
watch(
  () => props.isSubmitting,
  (submitting, wasSubmitting) => {
    if (wasSubmitting && !submitting && props.errorMessage === undefined) close()
  },
)

function close(): void {
  reset()
  open.value = false
}

const combinedError = computed(() => validationError.value ?? props.errorMessage)
const currencyOptions = Object.values(CurrencyCode).map((value) => ({ value, label: value }))
</script>

<template>
  <div>
    <ActionButton type="button" @click="open = true">
      {{ t('loans.addLoan') }}
    </ActionButton>

    <Dialog :open="open" :label="t('loans.addLoanTitle')" @close="close">
      <form class="flex w-[28rem] flex-col gap-4 p-[var(--space-block)]" @submit.prevent="submit">
        <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ t('loans.addLoanTitle') }}
        </h2>

        <TextField v-model="name" :label="t('loans.nameLabel')" maxlength="200" required autofocus />

        <div class="flex flex-col gap-1">
          <label
            for="loan-principal"
            class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]"
            >{{ t('loans.originalPrincipal') }}</label
          >
          <div class="flex items-start gap-[var(--space-cell)]">
            <Select
              v-model="currency"
              class="w-32"
              :options="currencyOptions"
              :placeholder="t('loans.selectCurrency')"
              :aria-label="t('loans.currency')"
            />
            <AmountInput
              id="loan-principal"
              v-model="principal"
              :currency="currency"
              :locale="currentLocale"
              required
            />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-[var(--space-cell)]">
          <label class="flex flex-col gap-1">
            <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
              t('loans.annualInterestRate')
            }}</span>
            <DecimalInput
              v-model="annualInterestRateText"
              :decimal-digits="4"
              :group-separator="numberFormat.groupSeparator"
              :decimal-separator="numberFormat.decimalSeparator"
              unit="%"
              :show-total="false"
            />
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
              t('loans.startDate')
            }}</span>
            <input
              v-model="drawdownDate"
              type="date"
              required
              :min="MIN_DATE"
              :max="todayDate"
              class="field-control"
            />
          </label>
        </div>

        <LoanTermsFields
          v-model:end-date="endDate"
          v-model:lender="lender"
          v-model:rate-type="rateType"
          v-model:loan-type="loanType"
          v-model:monthly-payment="monthlyPayment"
          :currency="currency"
          :locale="currentLocale"
        />

        <StatusTag v-if="combinedError" tone="danger">{{ combinedError }}</StatusTag>

        <div class="flex gap-2">
          <ActionButton type="submit" :disabled="isSubmitting">
            {{ isSubmitting ? t('loans.saving') : t('loans.createLoan') }}
          </ActionButton>
          <ActionButton type="button" variant="secondary" @click="close">
            {{ t('loans.cancel') }}
          </ActionButton>
        </div>
      </form>
    </Dialog>
  </div>
</template>
